/**
 * The managing association and its standing on Sunbiz: what this product may
 * say about it, and what it must never.
 *
 * Unscored context, loaded quarterly from two public files — the DBPR project
 * register, which names the association behind a development, and the
 * Division of Corporations' file, which says whether that corporation is
 * active. The wording is pinned here with tests, the treatment `closedCases.ts`
 * and `milestone.ts` get, because an inactive corporation is the one line in
 * the drawer that could be read as a verdict on a building.
 *
 * **"Inactive on Sunbiz" is a fact about a corporate filing.** It is not
 * "dissolved" — the file carries no reason, and an administrative dissolution
 * for a missed annual report is one — and it is never "abandoned", "defunct",
 * "delinquent" or "insolvent", each of which is a claim about the building or
 * its finances that nobody made. And an inactive association with a
 * `successor` — an active corporation whose name carries every word of this
 * one — may have been re-filed under it, so the line says the name is
 * *shared* and never that the one succeeded the other; "no active
 * corporation shares the name" may be said only when the field is null.
 *
 * **No status is not inactive.** `status === null` means the register named
 * an association the file did not join by name — a quarter of names, and
 * that is our failure to read a spelling, not theirs to file.
 */

import type { Association } from "../api/types";

/** The one-line summary for the drawer's cell. "" when there is nothing. */
export function associationLabel(a: Association | null | undefined): string {
  if (!a) return "";
  const name = titleCase(a.name);
  if (a.status === "active") return `${name} · active on Sunbiz`;
  if (a.status === "inactive") {
    return a.successor
      ? `${name} · inactive on Sunbiz; ${titleCase(a.successor)} is active`
      : `${name} · inactive on Sunbiz`;
  }
  return `${name} · no Sunbiz record joined`;
}

/** The sentence that explains the cell, for a tooltip or a caption. */
export function associationBasis(a: Association | null | undefined): string {
  if (!a) return "";
  const checked = a.checked ? ` as of the state's ${formatMonth(a.checked)} file` : "";
  if (a.status === "active") {
    return (
      `Named as the managing association in the Division of Condominiums' ` +
      `register, and an active corporation on Sunbiz${checked}.`
    );
  }
  if (a.status === "inactive") {
    const last = a.last_event ? `, last filing ${formatMonth(a.last_event)}` : "";
    const successor = a.successor
      ? ` An active corporation shares the name, so the association may ` +
        `simply have been re-filed under it.`
      : ` No active corporation shares the name. A corporation ` +
        `goes inactive for reasons the file does not give — a missed annual ` +
        `report is one — so this is a filing status, not a finding about the ` +
        `building.`;
    return (
      `Named as the managing association in the Division of Condominiums' ` +
      `register; its corporation is inactive on Sunbiz${checked}${last}.` +
      successor
    );
  }
  return (
    `Named as the managing association in the Division of Condominiums' ` +
    `register. No Sunbiz record was joined by name — a spelling the join ` +
    `does not recognise, not evidence of anything about the association.`
  );
}

/** Whether the cell deserves a notice below the table, and which kind. */
export function associationTone(
  a: Association | null | undefined,
): "neutral" | "none" {
  return a && a.status === "inactive" && !a.successor ? "neutral" : "none";
}

/** `YYYY-MM-DD` to `Jan 2004`; parsed by hand for `closedCases.ts`'s reason. */
export function formatMonth(value: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(value.trim());
  if (!match) return "";
  const index = Number(match[2]) - 1;
  if (index < 0 || index > 11) return "";
  return `${MONTHS[index]} ${match[1]}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The register writes names in capitals. Title-cased for reading, with the
 * abbreviations and Roman numerals the registers use left as they are.
 */
export function titleCase(name: string): string {
  return name
    .toLowerCase()
    .split(/\s+/)
    .map((word) =>
      KEEP_UPPER.has(word.toUpperCase())
        ? word.toUpperCase()
        : word.replace(/^([a-z])/, (c) => c.toUpperCase()),
    )
    .join(" ");
}

const KEEP_UPPER = new Set([
  "II", "III", "IV", "VI", "VII", "VIII", "IX", "XI", "XII", "LLC",
  "N", "S", "E", "W", "NE", "NW", "SE", "SW", "A1A", "US", "HOA",
]);
